/**
 * Bill Processing with WebSocket
 * Real-time bill processing without polling
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import BillWebSocketService from '../../services/BillWebSocketService';

const BillProcessingWS = () => {
  const { token } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [runId, setRunId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [billData, setBillData] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Initialize WebSocket service
  useEffect(() => {
    if (token) {
      wsRef.current = new BillWebSocketService(token);

      wsRef.current.onStatus((status) => {
        setConnectionStatus(status);
      });

      wsRef.current.onError((err) => {
        setError(err.message || 'WebSocket error');
      });
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.disconnect();
      }
    };
  }, [token]);

  // Subscribe to WebSocket messages
  useEffect(() => {
    if (!wsRef.current || !runId) return;

    const unsubscribers = [];

    unsubscribers.push(wsRef.current.onStepStarted((data) => {
      setCurrentStep(data.step_name);
      addMessage('step', `Starting: ${data.step_name}`, data);
    }));

    unsubscribers.push(wsRef.current.onStepCompleted((data) => {
      addMessage('step_complete', `Completed: ${data.step_name}`, data);
    }));

    unsubscribers.push(wsRef.current.onStepFailed((data) => {
      addMessage('error', `Failed: ${data.step_name} - ${data.error_message}`, data);
    }));

    unsubscribers.push(wsRef.current.onChatMessage((data) => {
      addMessage(data.message_type, data.content, data);
    }));

    unsubscribers.push(wsRef.current.onBillDataUpdate((data) => {
      setBillData(data.bill_data);
      addMessage('bill_update', 'Bill data updated', data);
    }));

    unsubscribers.push(wsRef.current.onWorkflowStatus((data) => {
      setWorkflowStatus(data.status);
      setCurrentStep(data.current_step);
    }));

    unsubscribers.push(wsRef.current.onWorkflowCompleted((data) => {
      setWorkflowStatus(data.success ? 'completed' : 'failed');
      addMessage(data.success ? 'success' : 'error',
        data.success ? 'Workflow completed!' : `Workflow failed: ${data.error_message}`,
        data);
    }));

    return () => unsubscribers.forEach(unsub => unsub());
  }, [runId]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const addMessage = (type, content, data = {}) => {
    setMessages(prev => [...prev, {
      id: Date.now() + Math.random(),
      type,
      content,
      timestamp: new Date().toISOString(),
      data
    }]);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);
    setMessages([]);
    setBillData(null);

    try {
      addMessage('system', `Uploading: ${file.name}`);
      const result = await wsRef.current.uploadBill(file);

      if (result.success) {
        setRunId(result.run_id);
        addMessage('system', `Upload complete. Run ID: ${result.run_id.slice(0,8)}...`);

        // Connect to WebSocket - all messages via WebSocket only
        wsRef.current.connect(result.run_id);
        addMessage('system', 'WebSocket connected. Waiting for real-time updates...');
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  // No polling - pure WebSocket only
  // Messages come via WebSocket events subscribed above

  const handleDisconnect = () => {
    wsRef.current?.disconnect();
    setRunId(null);
  };

  const getStatusColor = () => {
    if (connectionStatus === 'connected') return 'bg-green-500';
    if (connectionStatus === 'disconnected') return 'bg-red-500';
    return 'bg-yellow-500';
  };

  const getMessageStyles = (type) => {
    const styles = {
      step: { bg: 'bg-blue-50', border: 'border-blue-500', icon: '⚡' },
      step_complete: { bg: 'bg-green-50', border: 'border-green-500', icon: '✅' },
      assistant: { bg: 'bg-purple-50', border: 'border-purple-500', icon: '🤖' },
      tool_call: { bg: 'bg-orange-50', border: 'border-orange-500', icon: '🔧' },
      tool_result: { bg: 'bg-cyan-50', border: 'border-cyan-500', icon: '📊' },
      bill_update: { bg: 'bg-amber-50', border: 'border-amber-500', icon: '📄' },
      error: { bg: 'bg-red-50', border: 'border-red-500', icon: '❌' },
      success: { bg: 'bg-green-50', border: 'border-green-500', icon: '🎉' },
      system: { bg: 'bg-gray-50', border: 'border-gray-400', icon: '💬' }
    };
    return styles[type] || styles.system;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-3">
          Bill Processing (WebSocket)
        </h1>
        <div className="flex items-center gap-4 p-3 bg-gray-100 rounded-lg text-sm">
          <span className={`w-3 h-3 rounded-full ${getStatusColor()} animate-pulse`} />
          <span className="font-semibold capitalize">{connectionStatus}</span>
          {runId && (
            <span className="text-gray-500 font-mono bg-gray-200 px-2 py-1 rounded text-xs">
              {runId.slice(0, 8)}...
            </span>
          )}
          {workflowStatus && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase bg-blue-100 text-blue-700">
              {workflowStatus}
            </span>
          )}
          {currentStep && (
            <span className="text-gray-500 italic">Step: {currentStep}</span>
          )}
        </div>
      </div>

      {/* Upload Section */}
      <div className="flex gap-3 mb-6">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept=".pdf,.PDF"
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-lg
                     hover:from-indigo-600 hover:to-purple-700 transition-all duration-200
                     disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
        >
          {isUploading ? 'Uploading...' : 'Upload Bill PDF'}
        </button>
        {connectionStatus === 'connected' && (
          <button
            onClick={handleDisconnect}
            className="px-6 py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
          >
            Disconnect
          </button>
        )}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="flex justify-between items-center p-4 mb-6 bg-red-50 border border-red-300 rounded-lg text-red-700">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-xl font-bold hover:text-red-900">
            ×
          </button>
        </div>
      )}

      {/* Messages Section */}
      <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
        <div className="px-4 py-3 bg-gray-100 border-b border-gray-200 font-semibold text-gray-700">
          Real-time Updates ({messages.length})
        </div>
        <div className="max-h-96 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="py-10 text-center text-gray-400">
              Upload a bill to start processing with real-time updates
            </div>
          ) : (
            messages.map((msg) => {
              const style = getMessageStyles(msg.type);
              return (
                <div
                  key={msg.id}
                  className={`p-3 rounded-lg border-l-4 ${style.bg} ${style.border}`}
                >
                  <div className="flex items-center gap-2 mb-1 text-xs">
                    <span>{style.icon}</span>
                    <span className="font-semibold uppercase text-gray-600">{msg.type}</span>
                    <span className="text-gray-400 ml-auto">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700 break-words">
                    {typeof msg.content === 'string'
                      ? msg.content.substring(0, 500)
                      : JSON.stringify(msg.content).substring(0, 500)}
                    {msg.content?.length > 500 && '...'}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Bill Data Section */}
      {billData && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-amber-100 border-b border-amber-200 font-semibold text-amber-800">
            Bill Data
          </div>
          <pre className="p-4 bg-gray-50 text-xs overflow-x-auto max-h-64">
            {JSON.stringify(billData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default BillProcessingWS;
